import { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface ConfirmDialogProps {
  title: string;
  /** `danger` for something that destroys data; `warn` for something merely slow or disruptive. */
  tone: "danger" | "warn";
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A confirmation the reader can actually read.
 *
 * The browser's own confirm() was doing this job, and doing it badly: an unstyled OS box that says
 * nothing about consequence, cannot be told apart from a cookie prompt, and looks like something to
 * dismiss. Deleting a standard destroys an uploaded document, its extracted text and its whole section
 * tree — that deserves to be spelled out and to look like what it is.
 */
const ConfirmDialog = ({
  title,
  tone,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => (
  <div
    className="vr-backdrop vr-backdrop-top"
    role="dialog"
    aria-modal="true"
    aria-label={title}
  >
    <div className="vr-modal vr-modal-sm">
      <div className="vr-modal-head">
        <div className={`vr-confirm-head vr-confirm-${tone}`}>
          <span className="vr-confirm-icon">
            <AlertTriangle size={16} />
          </span>
          <h3>{title}</h3>
        </div>
      </div>

      <div className="vr-modal-body vr-confirm-body">{children}</div>

      <div className="vr-modal-foot">
        <button className="vr-btn vr-btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button className={`vr-btn vr-btn-confirm vr-confirm-${tone}`} onClick={onConfirm}>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

export default ConfirmDialog;
